import React, { useEffect, useState } from 'react';
import { chatlistService } from '../services/chatServices';
import UserShortCut from 'components/common/UserShortCut';
import {
  ChatData,
  ChatListItem,
  ChatUnreadCount,
  UserFilters,
} from '../types/types';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'hooks/useDebounce';
import { ChatListItemsSkeleton } from './ChatSkeletons';
import { getLastAction, handelActiveChat } from '../utils/renderSentMessage';
import { useSignalR } from 'contexts/SignalRProvider';
import { useAuthContext } from 'contexts/AuthContext';

const isImageFile = (fileName: string | undefined): boolean => {
  if (!fileName) return false;
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
};

const getPreviewLabel = (item: ChatListItem): string => {
  if (item.lastAction?.fileName) {
    if (isImageFile(item.lastAction.fileName)) {
      return 'Photo';
    }
    return 'Document';
  }
  return '';
};

interface NewChatListProps {
  onSelectUser: (user: ChatListItem) => void;
  isGroup: boolean;
  externalSearch?: string;
  onCount?: (count: number) => void;
  activeId?: string | null;
}
const UserList: React.FC<NewChatListProps> = ({
  onSelectUser,
  isGroup,
  externalSearch,
  onCount,
  activeId,
}) => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const [users, setUsers] = useState<ChatData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const useExternal = externalSearch !== undefined;

  const [filters, setFilters] = useState<UserFilters>({
    searchValue: '',
    lenght: 5,
    currentList: 0,
    id: 0,
    roleName: '',
    userid: '',
    roleId: '',
    ids: [],
    selectAll: false,
    isGroup: true,
  });
  const debouncedFilters = useDebounce(filters, 300);
  const { connection } = useSignalR();

  useEffect(() => {
    if (!connection) return;
    const handler = (data: ChatUnreadCount) => {
      setUsers((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          chatList: prev.chatList.map((item) => {
            const conv = data.byConversation.find(
              (c) => c.id === item.chatUser.value && c.isGroup === item.isGroup,
            );
            return { ...item, messageUnread: conv?.unread ?? 0 };
          }),
        };
      });
    };
    connection.on('ChatUnreadCountUpdated', handler);
    return () => {
      connection.off('ChatUnreadCountUpdated', handler);
    };
  }, [connection]);

  useEffect(() => {
    if (!useExternal) return;
    setFilters((prev) => ({
      ...prev,
      searchValue: externalSearch ?? '',
      currentList: 0,
    }));
  }, [externalSearch, useExternal]);

  useEffect(() => {
    const fetchUsers = async (): Promise<void> => {
      const response = await chatlistService(debouncedFilters);
      if (!response.success) {
        console.error(response.message, 'Failed to fetch chat list');
        return;
      }
      const res = response.data;
      setUsers((prev: ChatData | null) => {
        if (prev === null || filters.currentList === 0) {
          return {
            ...res,
            chatList: [...res.chatList],
          };
        }
        setIsLoading(false);
        return {
          ...prev,
          chatList: prev?.chatList.concat(res.chatList),
        };
      });
    };
    fetchUsers();
  }, [debouncedFilters]);

  useEffect(() => {
    if (onCount && users) onCount(users.chatList.length);
  }, [users, onCount]);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>): void => {
    const target = event.currentTarget;
    const scrollPosition = target.scrollTop + target.clientHeight;
    const threshold = 50;

    if (scrollPosition + threshold >= target.scrollHeight && !isLoading) {
      setIsLoading(true);
      setFilters((prev) => ({
        ...prev,
        currentList: prev.currentList + 1,
      }));
    }
  };

  const handleSearchChange = (value: string): void => {
    setSearchValue(value);
    setFilters((prev) => ({
      ...prev,
      searchValue: value,
      currentList: 0,
    }));
  };

  return (
    <div
      className={`tw-flex tw-flex-col tw-flex-1 tw-min-h-0 ${isGroup ? 'tw-hidden' : ''}`}
    >
      {/* Search (hidden when an external search is provided by the parent) */}
      {!useExternal && (
        <div className="tw-px-4 tw-pt-3 tw-pb-3 tw-border-b tw-border-gray3">
          <div className="tw-relative">
            <span className="tw-absolute tw-left-4 tw-top-1/2 tw--translate-y-1/2 tw-pointer-events-none tw-flex tw-items-center">
              <img
                className="tw-w-4 tw-h-4 tw-icon-gray"
                src="/icons/search.svg"
                alt=""
              />
            </span>
            <input
              type="text"
              value={searchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={t('chat.search.placeholder', {
                defaultValue: 'Search people or conversations',
              })}
              className="tw-w-full tw-h-10 tw-rounded-full tw-bg-white tw-border tw-pl-11 tw-pr-10 tw-text-sm tw-text-dark tw-outline-none focus:tw-border-primary focus:tw-ring-2 focus:tw-ring-primary-opacity tw-transition tw-placeholder:tw-text-dark-gray4"
            />
            {searchValue && (
              <button
                type="button"
                onClick={() => handleSearchChange('')}
                className="tw-absolute tw-right-2 tw-top-1/2 tw--translate-y-1/2 tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-full tw-bg-transparent hover:tw-bg-light-gray2 tw-border-none tw-cursor-pointer tw-transition"
                aria-label="Clear search"
              >
                <img
                  className="tw-w-3 tw-h-3 tw-icon-gray"
                  src="/icons/close.svg"
                  alt=""
                />
              </button>
            )}
          </div>
        </div>
      )}

      {/* List */}
      <div
        className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-custom-scrollbar"
        id="users-list"
        onScroll={handleScroll}
      >
        {users ? (
          users.chatList.length > 0 ? (
            <div className="tw-divide-y tw-divide-light-gray3">
              {users?.chatList.map((item, index) => {
                const isUnread = item.messageUnread > 0;
                const hasFile = !!item.lastAction?.fileName;
                const isImage =
                  hasFile && isImageFile(item.lastAction?.fileName);
                const previewLabel = getPreviewLabel(item);
                const previewText = hasFile
                  ? previewLabel
                  : getLastAction(item);
                const hasPreview = previewText.length > 0;
                const isReceived = item.lastAction?.userId !== user.userId;
                return (
                  <div
                    key={index}
                    className={`tw-relative tw-grid tw-items-center tw-gap-3 tw-px-[calc(var(--unit-minthr)*22)] tw-py-1.5 tw-cursor-pointer tw-transition ${
                      isUnread
                        ? 'tw-bg-primary-opacity/40'
                        : 'tw-bg-transparent hover:tw-bg-primary-light4'
                    } ${item.groupsEnd ? 'groups-end' : ''} ${item.isGroup ? 'group' : ''}`}
                    style={{
                      gridTemplateColumns:
                        'calc(var(--unit-minthr)*40) 1fr auto',
                    }}
                    data-id={item.chatUser?.value}
                    onClick={(e) => {
                      handelActiveChat(e);
                      onSelectUser(item);
                    }}
                  >
                    {/* Avatar + status dot */}
                    <div className="tw-relative tw-w-9 tw-h-9">
                      {!item.isGroup ? (
                        <UserShortCut
                          className=" tw-w-9 tw-h-9 tw-rounded-full tw-object-cover"
                          src={`upload/profileimages/${item.chatUser?.value}/${item.chatUser?.imagePath}`}
                          alt=""
                        />
                      ) : item.chatUser.imagePath != null ? (
                        <img
                          className=" tw-w-9 tw-h-9 tw-rounded-full tw-object-cover"
                          src=""
                          alt=""
                        />
                      ) : (
                        <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-primary-opacity tw-flex tw-items-center tw-justify-center">
                          <img
                            className="tw-w-[calc(var(--unit-minthr)*18)] tw-h-[calc(var(--unit-minthr)*18)] tw-icon-primary"
                            src="/icons/Group 65.svg"
                            alt=""
                          />
                        </div>
                      )}
                    </div>

                    {/* Meta: name + preview */}
                    <div className="tw-min-w-0">
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <span
                          className={`tw-truncate tw-text-[calc(var(--unit-minthr)*13)] tw-leading-tight name ${
                            isUnread
                              ? 'tw-font-bold tw-text-dark'
                              : 'tw-font-semibold tw-text-dark'
                          }`}
                        >
                          {item.chatUser.text}
                        </span>
                      </div>
                      <div className="tw-mt-0.5 tw-flex tw-items-center tw-gap-1 lastmessage">
                        {hasPreview && (
                          <svg
                            className={`tw-shrink-0 ${isReceived ? 'tw-text-dark-gray4' : 'pre-icon'}`}
                            width="11"
                            height="11"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            {hasFile ? (
                              isImage ? (
                                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                              ) : (
                                <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
                              )
                            ) : isReceived ? (
                              <path d="M21 15a4 4 0 0 1-4 4H8l-5 4V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
                            ) : (
                              <>
                                <path d="M12 20h9" />
                                <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" />
                              </>
                            )}
                          </svg>
                        )}
                        <span
                          className={`tw-truncate tw-text-[calc(var(--unit-minthr)*12)] tw-flex-1 ${
                            !hasPreview
                              ? 'tw-italic tw-text-dark-gray4'
                              : isUnread
                                ? 'tw-font-medium tw-text-dark-gray2'
                                : 'tw-text-dark-gray3'
                          }`}
                        >
                          {hasPreview
                            ? previewText
                            : t('chat.start.conversation', {
                                defaultValue: 'Start a conversation',
                              })}
                        </span>
                      </div>
                    </div>

                    {/* Right column: time + badge */}
                    <div className="tw-flex tw-flex-col tw-items-end tw-gap-1 tw-min-w-[calc(var(--unit-minthr)*40)]">
                      <span
                        className={`tw-text-[calc(var(--unit-minthr)*11)] tw-whitespace-nowrap tw-tabular-nums tw-leading-none date ${
                          isUnread
                            ? 'tw-font-bold tw-text-primary'
                            : 'tw-font-medium tw-text-dark-gray4'
                        }`}
                      >
                        {item.lastAction?.date
                          ? moment(item.lastAction.date).format('HH:mm')
                          : '—'}
                      </span>
                      {isUnread && (
                        <span
                          className="tw-inline-grid tw-place-items-center tw-min-w-[calc(var(--unit-minthr)*18)] tw-h-[calc(var(--unit-minthr)*18)] tw-px-1 tw-rounded-full tw-bg-primary tw-text-[calc(var(--unit-minthr)*10)] tw-font-bold tw-text-white"
                          data-value={item.messageUnread}
                        >
                          {item.messageUnread > 99 ? '99+' : item.messageUnread}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full tw-text-center tw-py-10 tw-px-6">
              <p className="tw-text-sm tw-text-dark-gray3 tw-m-0">
                {t('chat.no.users', {
                  defaultValue: 'No conversations match your search.',
                })}
              </p>
            </div>
          )
        ) : (
          <ChatListItemsSkeleton />
        )}
      </div>
    </div>
  );
};

export default UserList;
